import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BasicInfoFields, PriceLocationFields, DescriptionField, ContactFields, FormActions } from "@/components/listing-form/ListingFields";
import { ImageUploader } from "@/components/listing-form/ImageUploader";

const EMPTY_FORM = {
  title: "", description: "", price: "", currency: "USD", category: "vehicles",
  location: "", condition: "used", images: [], contact_email: "", contact_phone: "",
};

const toFormState = (d) => ({
  ...EMPTY_FORM,
  title: d.title, description: d.description, price: d.price ?? "", currency: d.currency || "USD",
  category: d.category, location: d.location, condition: d.condition, images: d.images || [],
  contact_email: d.contact_email || "", contact_phone: d.contact_phone || "",
});

const toPayload = (f) => ({ ...f, price: f.price === "" || f.price == null ? null : parseFloat(f.price) });

export default function ListingForm({ mode = "create" }) {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isEdit && id) api.get(`/listings/${id}`).then(({ data }) => setForm(toFormState(data)));
  }, [isEdit, id]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setImages = (updater) => setForm((p) => ({ ...p, images: updater(p.images) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = toPayload(form);
      const res = isEdit ? await api.put(`/listings/${id}`, payload) : await api.post("/listings", payload);
      toast.success(isEdit ? "Listing updated" : "Listing posted!");
      navigate(`/listing/${res.data.listing_id}`);
    } catch (err) {
      toast.error("Failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-8 mt-8" data-testid="listing-form-page">
      <h1 className="font-display text-3xl md:text-5xl uppercase border-b-2 border-black pb-2">/ {isEdit ? "Edit Ad" : "Post New Ad"}</h1>
      <form onSubmit={submit} className="mt-6 space-y-5 border-2 border-black p-6 bg-white">
        <BasicInfoFields form={form} set={set} />
        <PriceLocationFields form={form} set={set} />
        <DescriptionField form={form} set={set} />
        <ContactFields form={form} set={set} />
        <ImageUploader images={form.images} onChange={setImages} />
        <FormActions submitting={submitting} isEdit={isEdit} onCancel={() => navigate(-1)} />
      </form>
    </div>
  );
}
